// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Text } from 'grommet'
import { Copy } from 'grommet-icons'

import {
  FormInput,
  ModalDialog,
  ModalHeader,
  Notification,
  Typography
} from '../../../../components'
import { del, post } from '../../../../utils/api-utils'
import { displayApiError } from '../../../../utils/error-handling-utils'

const ReplaceCredentailsModal = ({
  setShowReplaceCredentailsModal,
  onSuccess,
  scimId,
  scimCredentialId,
  scimName
}) => {
  const { t } = useTranslation(['authz', 'common'])

  const [showNewCredentails, setShowNewCredentails] = useState(false)
  const [apiErrorNotification, setApiErrorNotification] = useState(null)
  const [loading, setLoading] = useState(false)
  const [scimCredentials, setscimCredentials] = useState({})
  const APIEndpoint = `${window.location.origin}/identity/v2alpha1/scim/v2`

  const replaceCredentails = () => {
    setLoading(true)
    del(
      `/internal-identity/v2alpha1/scim-integrations/${scimId}/scim-credentials/${scimCredentialId}`
    ).then(
      () => {
        post(
          `/internal-identity/v2alpha1/scim-integrations/${scimId}/scim-credentials`,
          {
            credentialType: 'SCIM_KEY'
          }
        )
          .then((res) => {
            setscimCredentials(res?.data)
            setLoading(false)
            setShowNewCredentails(true)
            onSuccess()
          })
          .catch((error) => {
            setLoading(false)
            setApiErrorNotification(
              displayApiError(error, t, setApiErrorNotification)
            )
          })
      },
      (error) => {
        setLoading(false)
        setApiErrorNotification(
          displayApiError(error, t, setApiErrorNotification)
        )
      }
    )
  }

  const copyValueToClipboard = (value) => {
    navigator.clipboard.writeText(value)
  }

  return (
    <>
      <ModalDialog
        overflow="hidden"
        position="right"
        height="100%"
        testId="replace-credentails-modal-dialog"
        header={
          <ModalHeader
            title={
              <Typography
                type="heading"
                level="2"
                testId="replace-credentails-heading"
              >
                {showNewCredentails
                  ? t('iam:external_scim_integrations.new_crendetial_generated')
                  : t('iam:external_scim_integrations.replace_credentails')}
              </Typography>
            }
          />
        }
        content={
          <>
            {apiErrorNotification}
            {showNewCredentails ? (
              <Box gap="xsmall" width="medium">
                <Typography type="text" margin={{ top: 'small' }} size="medium">
                  {t(
                    'iam:external_scim_integrations.new_credential_generated_desc'
                  )}
                </Typography>
                <Typography type="text" margin={{ top: 'small' }} size="medium">
                  {t(
                    'iam:external_scim_integrations.new_credential_generated_desc_2'
                  )}
                </Typography>

                <Box gap="medium">
                  <FormInput
                    name="scim-access-token"
                    testId="scim-access-token"
                    disabled
                    label={t(
                      'iam:external_scim_integrations.scim_client_secret'
                    )}
                    value={scimCredentials?.scimKey}
                    action={
                      <Button
                        default
                        hoverIndicator="none"
                        onClick={() =>
                          copyValueToClipboard(scimCredentials?.scimKey)
                        }
                        icon={<Copy size="medium" />}
                        testId="copy-btn"
                      />
                    }
                    inputType="text"
                  />

                  <FormInput
                    name="scim-api-endpoint"
                    testId="scim-api-endpoint"
                    disabled
                    label={t(
                      'iam:external_scim_integrations.scim_api_endpoint'
                    )}
                    value={APIEndpoint}
                    action={
                      <Button
                        default
                        hoverIndicator="none"
                        onClick={() => copyValueToClipboard(APIEndpoint)}
                        icon={<Copy size="medium" />}
                        testId="copy-btn"
                      />
                    }
                    inputType="text"
                  />
                  <Box direction="row" style={{ justifyContent: 'flex-end' }}>
                    <Button
                      label={t('common:close')}
                      primary
                      onClick={() => setShowReplaceCredentailsModal(false)}
                      data-testId="close-button"
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box gap="xsmall" width="medium">
                <Notification
                  margin="small"
                  type="inline"
                  text={t(
                    'iam:external_scim_integrations.replace_crendetial_notification'
                  )}
                  status="warning"
                  testId="replace-credentails-warning"
                />
                <Typography type="text" margin={{ top: 'small' }} size="medium">
                  <Trans
                    i18nKey={t(
                      'iam:external_scim_integrations.replace_credential_desc',
                      {
                        name: scimName
                      }
                    )}
                    components={<Text color="text-strong" weight={500} />}
                  />
                </Typography>

                <Box
                  direction="row"
                  justify="end"
                  gap="medium"
                  margin={{ top: 'medium' }}
                >
                  <Button
                    label={t('common:cancel')}
                    onClick={() => setShowReplaceCredentailsModal(false)}
                    data-testId="cancel-button"
                    disabled={loading}
                  />
                  <Button
                    label={t(
                      'iam:external_scim_integrations.replace_credentails'
                    )}
                    primary
                    onClick={replaceCredentails}
                    data-testId="replace-credentails-btn"
                    disabled={loading}
                  />
                </Box>
              </Box>
            )}
          </>
        }
        onClose={() => setShowReplaceCredentailsModal(false)}
      />
    </>
  )
}
ReplaceCredentailsModal.propTypes = {
  setShowReplaceCredentailsModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  scimId: PropTypes.string.isRequired,
  scimCredentialId: PropTypes.string.isRequired,
  scimName: PropTypes.string.isRequired
}

export default ReplaceCredentailsModal
